import { supabase } from './client';

const CONTACT_TABLE = 'store_office_chat_contacts';
let contactTableAvailability: boolean | null = null;

const isMissingTableError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.code || '').toLowerCase();
  return code === '42p01'
    || code === 'pgrst205'
    || message.includes('does not exist')
    || message.includes('could not find the table')
    || message.includes('schema cache');
};

const normalizeIds = (userIds: string[]): string[] => {
  return Array.from(new Set((userIds || []).filter(Boolean)));
};

export const getStoreOfficeChatContactMemberIds = async (storeId: string): Promise<string[]> => {
  if (!storeId) return [];
  if (contactTableAvailability === false) return [];

  const { data, error } = await supabase
    .from(CONTACT_TABLE)
    .select('user_id')
    .eq('store_id', storeId);

  if (error) {
    if (isMissingTableError(error)) {
      contactTableAvailability = false;
      return [];
    }
    throw error;
  }

  contactTableAvailability = true;

  return normalizeIds((data || []).map((row: any) => row.user_id));
};

export const saveStoreOfficeChatContactMemberIds = async (
  storeId: string,
  userIds: string[]
): Promise<void> => {
  if (!storeId) throw new Error('storeId is required');
  if (contactTableAvailability === false) {
    throw new Error('Falta crear la tabla de contactos de oficina (migration pendiente).');
  }

  const normalizedIds = normalizeIds(userIds);

  const { error: deleteError } = await supabase
    .from(CONTACT_TABLE)
    .delete()
    .eq('store_id', storeId);

  if (deleteError) {
    if (isMissingTableError(deleteError)) {
      contactTableAvailability = false;
      throw new Error('Falta crear la tabla de contactos de oficina (migration pendiente).');
    }
    throw deleteError;
  }

  if (normalizedIds.length === 0) return;

  const rows = normalizedIds.map((userId) => ({
    store_id: storeId,
    user_id: userId,
  }));

  const { error: insertError } = await supabase
    .from(CONTACT_TABLE)
    .insert(rows);

  if (insertError) {
    if (isMissingTableError(insertError)) {
      contactTableAvailability = false;
      throw new Error('Falta crear la tabla de contactos de oficina (migration pendiente).');
    }
    throw insertError;
  }

  contactTableAvailability = true;
};

export const resolveStoreOfficeChatRecipientIds = async (params: {
  storeId: string;
  fallbackMemberIds: string[];
  currentUserId?: string | null;
}): Promise<string[]> => {
  const { storeId, fallbackMemberIds, currentUserId } = params;

  const fallback = normalizeIds(fallbackMemberIds).filter((id) => id !== currentUserId);

  if (!storeId) return fallback;

  const configured = await getStoreOfficeChatContactMemberIds(storeId);
  if (configured.length === 0) return fallback;

  // Configured office contacts are the source of truth. We only remove current user.
  const filteredConfigured = normalizeIds(configured).filter((id) => id !== currentUserId);

  return filteredConfigured.length > 0 ? filteredConfigured : fallback;
};
